import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";

gsap.registerPlugin(ScrollTrigger, Draggable, InertiaPlugin);

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

let mm = gsap.matchMedia();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

// Create the guides
const guides = document.createElement("div");
guides.className = "guides wrap";
const guidesInner = document.createElement("div");
guidesInner.className = "inner grid";
guides.appendChild(guidesInner);
for (let i = 0; i < 8; i++) {
  const guide = document.createElement("div");
  guide.className = "guides__guide";
  guidesInner.appendChild(guide);
}
document.body.appendChild(guides);

// Set up a tween that animates the guides in and out
let toggleGuides = gsap
  .to(guides, {
    duration: 0.25,
    ease: "power2.inOut",
    autoAlpha: 1,
    paused: true,
  })
  .reverse();

// Check if the guides was enabled before
if (localStorage.getItem("debug") == "true") {
  toggleGuides.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener("keydown", (e) => {
  if (e.key === "g" && e.ctrlKey) {
    // Toggle the guides when ⌃G is pressed
    toggleGuides.reversed() ? toggleGuides.play() : toggleGuides.reverse();
    // Save the state in local storage
    localStorage.setItem("debug", !toggleGuides.reversed());
  }
});

guides.dataset.windowWidth = window.innerWidth;

window.addEventListener("resize", () => {
  guides.dataset.windowWidth = window.innerWidth;
});

/* -------------------------------------------------- */
/* 💆‍♂️ Header */
/* -------------------------------------------------- */

let header = document.querySelector(".header");
let headerClaimsWrap = document.querySelector(".header__claims");
let headerClaims = document.querySelectorAll(".header__claim");

if (headerClaimsWrap) {
  mm.add(
    {
      isDesktop: `(min-width: 1001px)`,
      isMobile: `(max-width: 1000px)`,
    },
    (context) => {
      let { isDesktop, isMobile } = context.conditions;

      gsap
        .timeline({
          defaults: {
            ease: "power1.inOut",
          },
          scrollTrigger: {
            start: 0,
            end: "+=100",
            toggleActions: "none play reverse none",
          },
        })

        .to(headerClaims, {
          autoAlpha: 0,
          duration: 0.25,
          stagger: -0.1,
        })

        .to(
          header,
          {
            height: isDesktop ? "5rem" : "4rem",
            duration: 1,
            ease: "power4.inOut",
          },
          "-=.2"
        );
    }
  );
}

/* -------------------------------------------------- */
/* 📱 Mobile Menu */
/* -------------------------------------------------- */

let menu = document.querySelector(".menu");
let logo = document.querySelector(".logo");
let menuToggle = document.querySelector(".menu-toggle");
let menuItems = document.querySelectorAll(".menu__item");

let menuAnimation = gsap
  .timeline({
    defaults: {
      ease: "power1.inOut",
    },
    paused: true,
  })

  .to(menu, {
    autoAlpha: 1,
  })

  .to(
    menuToggle.querySelector("line:nth-child(1)"),
    {
      transformOrigin: "center",
      rotate: 45,
      y: 14.5,
    },
    "<"
  )

  .to(
    menuToggle.querySelector("line:nth-child(2)"),
    {
      transformOrigin: "center",
      autoAlpha: 0,
    },
    "<"
  )

  .to(
    menuToggle.querySelector("line:nth-child(3)"),
    {
      transformOrigin: "center",
      rotate: -45,
      y: -14.5,
    },
    "<"
  )

  .reverse();

menuToggle.addEventListener("click", () => {
  menuToggle.setAttribute(
    "aria-expanded",
    menuAnimation.reversed() ? true : false
  );
  menuAnimation.reversed() ? menuAnimation.play() : menuAnimation.reverse();
});

menuItems.forEach((menuItem) => {
  menuItem.addEventListener("click", () => {
    if (window.matchMedia("(max-width: 1000px)").matches) {
      menuAnimation.reverse();
      menuToggle.setAttribute("aria-expanded", false);
    }
  });
});

logo.addEventListener("click", () => {
  if (window.matchMedia("(max-width: 1000px)").matches) {
    menuAnimation.reverse();
    menuToggle.setAttribute("aria-expanded", false);
  }
});

/* -------------------------------------------------- */
/* 📜 Scroll menu */
/* -------------------------------------------------- */

let blocks = document.querySelectorAll(".block");

if (blocks.length) {
  blocks.forEach((block) => {
    let menuItem = document.querySelector(`[href="#${block.id}"]`);

    if (!menuItem) return;

    ScrollTrigger.create({
      trigger: block,
      start: "top center",
      end: "bottom center",
      onToggle: (self) => {
        if (self.isActive) {
          menuItem.setAttribute("aria-active", true);
        } else {
          menuItem.removeAttribute("aria-active");
        }
      },
    });
  });
}

/* -------------------------------------------------- */
/* 🔄 Process */
/* -------------------------------------------------- */

let process = document.querySelector(".process");
let processItems = document.querySelectorAll(".process__item, .process__arrow");

if (process) {
  gsap
    .timeline({
      defaults: {
        ease: "power1.inOut",
      },
      scrollTrigger: {
        trigger: processItems[0],
        endTrigger: process,
        start: "bottom bottom",
        toggleActions: "play none none none",
      },
    })

    .from(processItems, {
      autoAlpha: 0,
      duration: 1,
      stagger: 0.25,
    });
}

/* -------------------------------------------------- */
/* 🫧 Bubbles */
/* -------------------------------------------------- */

let bubblesWrap = document.querySelector(".bubbles");
let bubbles = document.querySelectorAll(".bubble");

if (bubblesWrap) {
  // Set initial random position and opacity
  bubbles.forEach((bubble) => {
    gsap.set(bubble, {
      x: () => Math.random() * (bubblesWrap.clientWidth - bubble.clientWidth),
      y: () => Math.random() * (bubblesWrap.clientHeight - bubble.clientHeight),
      scale: 0.9,
      autoAlpha: 0,
    });

    // Make the bubbles draggable
    bubble.draggableInstance = Draggable.create(bubble, {
      type: "x,y",
      bounds: bubblesWrap,
      throwResistance: 1000,
      edgeResistance: 0.25,
      inertia: true,
      allowContextMenu: true,
    });
  });

  window.addEventListener("resize", () => {
    bubbles.forEach((bubble) => {
      bubble.draggableInstance[0].applyBounds();
    });
  });

  gsap.to(bubbles, {
    scale: 1,
    autoAlpha: 1,
    stagger: 0.1,
    duration: 0.5,
    ease: "back.out",
    scrollTrigger: {
      trigger: bubblesWrap,
      start: "top center",
      once: true,
    },
  });
}

/* -------------------------------------------------- */
/* Services Carousel */
/* -------------------------------------------------- */

let servicesWrap = document.querySelector(".services");
let services = document.querySelectorAll(".services__item");

if (servicesWrap) {
  // Create a container for the indicators
  let indicatorsWrap = document.createElement("div");
  indicatorsWrap.className = "services__indicators";
  servicesWrap.parentNode.insertBefore(
    indicatorsWrap,
    servicesWrap.nextSibling
  );

  services.forEach((service) => {
    // Create an indicator for each service
    let indicator = document.createElement("button");
    indicator.className = "services__indicator";
    indicatorsWrap.appendChild(indicator);

    // Set aria-active on scroll
    ScrollTrigger.create({
      trigger: service,
      start: "left center",
      end: "right center",
      horizontal: true,
      scroller: servicesWrap,
      onToggle: (self) => {
        indicator.setAttribute("aria-active", self.isActive);
      },
    });

    // Handle click on indicator
    indicator.addEventListener("click", function () {
      service.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    });
  });
}

// --------------------------------------------------
// 📩 Newsletter Popup
// --------------------------------------------------

const newsletterPopup = document.querySelector(".newsletter-popup");
const newsletterPopupInner = document.querySelector(".newsletter-popup__inner");
const newsletterForm = document.querySelector(".newsletter-form");
const newsletterClose = document.querySelector(
  ".newsletter-popup__close-button"
);

if (newsletterPopup) {
  // Open popup if it has not been closed before
  if (!sessionStorage.getItem("newsletterClosed")) {
    newsletterPopup.showModal();

    gsap.fromTo(
      newsletterPopupInner,
      {
        opacity: 0,
        scale: 0.95,
        y: -50,
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.5,
        ease: "power3.out",
      }
    );

    newsletterPopup.querySelector("input").focus();
  }

  function closePopup() {
    newsletterPopup.close();
    sessionStorage.setItem("newsletterClosed", true);
  }

  // Handle close button
  newsletterClose.addEventListener("click", () => {
    closePopup();
  });

  // Handle click outside to close
  newsletterPopup.addEventListener("click", (e) => {
    if (e.target === newsletterPopup) {
      closePopup();
    }
  });

  // Handle submit
  newsletterForm.addEventListener("submit", (e) => {
    e.preventDefault();
    const errorEl = newsletterForm.querySelector(".newsletter-form__error");
    const successEl = newsletterForm.querySelector(".newsletter-form__success");
    const mathInput = newsletterForm.querySelector(".newsletter-form__math-input");
    const fieldsEl = newsletterForm.querySelector(".newsletter-form__fields");

    if (errorEl) errorEl.classList.remove("is-visible");

    fetch(newsletterForm.action, {
      method: "POST",
      body: new FormData(newsletterForm),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error === "math") {
          if (errorEl) errorEl.classList.add("is-visible");
          if (mathInput) {
            mathInput.value = "";
            mathInput.focus();
          }
        } else {
          if (fieldsEl) fieldsEl.style.display = "none";
          if (successEl) successEl.classList.add("is-visible");
        }
      })
      .catch(function (error) {
        console.log("Error", error);
      });
  });
}
